import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type {
  QrcodeErrorCallback,
  QrcodeSuccessCallback,
  QrDimensionFunction
} from 'html5-qrcode/esm/core'
import type { Html5QrcodeCameraScanConfig } from 'html5-qrcode/esm/html5-qrcode'
import { useLocation, useSearchParams } from 'react-router'
import { ErrorMessage, Mtds, useSystemLog } from 'prove-common'
import ScanError from '../../ScanError'
import { pushMatomoEvent } from '../../utils/matomoUtils'
import { minimumQrboxSize, normalizeQrbox } from './qrboxUtils'

interface Html5QrcodePluginProps {
  fps: number
  qrbox: number | QrDimensionFunction
  qrCodeSuccessCallback: QrcodeSuccessCallback
  qrCodeErrorCallback?: QrcodeErrorCallback
  fullScreen?: boolean
}

const qrcodeRegionId = 'html5qr-code-full-region'
let html5QrCode: Html5Qrcode | undefined
let timeout: ReturnType<typeof setTimeout> | undefined
const isScanning = (): boolean =>
  html5QrCode?.getState() === Html5QrcodeScannerState.SCANNING
const isNotStarted = (): boolean =>
  html5QrCode?.getState() === Html5QrcodeScannerState.NOT_STARTED

const Html5QrcodePlugin: React.FC<Html5QrcodePluginProps> = ({
  fps,
  qrbox,
  qrCodeSuccessCallback,
  qrCodeErrorCallback,
  fullScreen = true
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [maxWidth, setMaxWidth] = useState<number>(-1)
  const [scannerRegionReady, setScannerRegionReady] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const systemLogMutation = useSystemLog()
  const [startScanError, setStartScanError] = useState<string | undefined>(
    undefined
  )
  const [scanning, setScanning] = useState(isScanning)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const ref = useRef<HTMLDivElement>(null)
  const getMaxWidth = useCallback(
    (containerHeight: number): number =>
      Math.min(containerHeight, window.innerWidth),
    []
  )

  const updateScannerRegionReady = useCallback((): void => {
    const containerElement = fullScreen ? ref.current : wrapperRef.current
    const width = containerElement?.clientWidth ?? 0
    const height = containerElement?.clientHeight ?? 0

    if (fullScreen) {
      const nextMaxWidth = getMaxWidth(height)
      setMaxWidth(nextMaxWidth)
      setScannerRegionReady(nextMaxWidth >= minimumQrboxSize)
      return
    }

    setScannerRegionReady(
      width >= minimumQrboxSize && height >= minimumQrboxSize
    )
  }, [fullScreen, getMaxWidth])

  const startScanner = useCallback((): void => {
    if (!scannerRegionReady) return

    if (!isNotStarted() && !isScanning() && startScanError == null) {
      const dynamicQrbox: QrDimensionFunction | number =
        typeof qrbox === 'function' && !fullScreen
          ? (w, h) => ({ width: Math.min(w, h), height: Math.min(w, h) })
          : qrbox
      const config: Html5QrcodeCameraScanConfig = {
        fps,
        qrbox: normalizeQrbox(dynamicQrbox),
        ...(fullScreen ? { aspectRatio: 1 } : {})
      }

      html5QrCode ??= new Html5Qrcode(qrcodeRegionId)
      html5QrCode
        .start(
          { facingMode: 'environment' },
          config,
          qrCodeSuccessCallback,
          qrCodeErrorCallback
        )
        .then(() => {
          setScanning(true)
          console.log('Scanner started')
        })
        .catch((err) => {
          console.log('Start scanner failed', err)
          html5QrCode = undefined
          setStartScanError(err)
          setScanning(false)
          pushMatomoEvent('html5qrcode_camera_error', 'camera_error')
          systemLogMutation.mutate({
            type: 'ERROR',
            message:
              "ProveScan: Couldn't start scanning " +
              JSON.stringify(err) +
              ' ' +
              'Context: ' +
              globalThis.location.toString() +
              '' +
              globalThis.navigator.userAgent,
            timestamp: Date.now().toString()
          })
          const updatedSearchParams = new URLSearchParams(
            searchParams.toString()
          )
          updatedSearchParams.set('manualEntry', 'true')
          setSearchParams(updatedSearchParams.toString(), {
            replace: true,
            state: location.state
          })
        })
    }
  }, [
    scannerRegionReady,
    startScanError,
    qrbox,
    fullScreen,
    fps,
    qrCodeSuccessCallback,
    qrCodeErrorCallback,
    systemLogMutation,
    searchParams,
    setSearchParams,
    location.state
  ])

  const stopScanner = useCallback((): void => {
    if (isScanning() && html5QrCode != null) {
      html5QrCode
        .stop()
        .then(() => {
          setScanning(false)
          console.log('Scanner stopped')
        })
        .catch((err) => {
          console.log('Stop scanner failed', err)
        })
        .finally(() => {
          html5QrCode = undefined
        })
    }
  }, [])

  useEffect(() => {
    const containerElement = fullScreen ? ref.current : wrapperRef.current
    if (containerElement == null) return

    updateScannerRegionReady()
    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateScannerRegionReady)
    resizeObserver.observe(containerElement)

    return () => resizeObserver.disconnect()
  }, [fullScreen, updateScannerRegionReady])

  useEffect(() => {
    if (scannerRegionReady && !isResizing) {
      startScanner()
    }
    if (isResizing) {
      stopScanner()
    }
  }, [scannerRegionReady, isResizing, startScanner, stopScanner])

  useEffect(() => {
    const handleResize = (): void => {
      // Guard against undefined during initial renders
      if (timeout != null) clearTimeout(timeout)
      setIsResizing(true)
      timeout = setTimeout(() => {
        setIsResizing(false)
        updateScannerRegionReady()
      }, 500)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (timeout != null) {
        clearTimeout(timeout)
        timeout = undefined
      }
    }
  }, [updateScannerRegionReady])

  useEffect(() => {
    return () => {
      stopScanner()
    }
  }, [stopScanner])

  // // Overstyr video-elementet slik at det holder seg innenfor parent
  useEffect(() => {
    if (!fullScreen && wrapperRef.current) {
      const container = wrapperRef.current
      // Observer for å fange videoen når Html5Qrcode legger den til
      const observer = new MutationObserver(() => {
        const videoEl = container.querySelector('video') as HTMLVideoElement
        const canvasEl = container.querySelector('canvas') as HTMLCanvasElement
        if (videoEl) {
          videoEl.style.objectFit = 'cover'
          videoEl.style.width = '100%'
          videoEl.style.height = '100%'
        }
        if (canvasEl) {
          canvasEl.style.width = '100%'
          canvasEl.style.height = '100%'
        }
      })

      observer.observe(container, { childList: true, subtree: true })

      // Cleanup
      return () => observer.disconnect()
    }
  }, [fullScreen])
  if (startScanError != null) {
    return (
      <div className={'m-4'}>
        <ErrorMessage error={new ScanError(startScanError)} />
      </div>
    )
  }
  const containerStyle: React.CSSProperties = fullScreen
    ? { position: 'absolute', inset: 0 }
    : { width: '100%', height: '100%' }

  return (
    <>
      {fullScreen ? (
        <>
          <div className={'absolute inset-0 top-16'}>
            {!scanning && (
              <div className='h-full flex justify-center items-center'>
                <Mtds.Spinner>Åpner kamera...</Mtds.Spinner>
              </div>
            )}
          </div>
          <div
            ref={ref}
            className={
              'absolute flex items-center justify-center inset-0 top-16'
            }
          >
            <div
              style={{
                width: '100%',
                maxWidth: `${maxWidth ?? 0}px`
              }}
              id={qrcodeRegionId}
            />
          </div>
        </>
      ) : (
        <div
          ref={wrapperRef}
          style={containerStyle}
          className={
            fullScreen ? '' : 'relative flex items-center justify-center'
          }
        >
          <div id={qrcodeRegionId} style={{ width: '100%', height: '100%' }} />
          {!scanning && (
            <div className='absolute inset-0 flex items-center justify-center'>
              <Mtds.Spinner>Åpner kamera...</Mtds.Spinner>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default Html5QrcodePlugin
